import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { getLeadByPhone } from "@/lib/leads-api";
import { colors, fonts, gradients, radius, shadows, spacing } from "@/lib/theme";

function PinwheelLogo({ size = 34 }: { size?: number }) {
    const half = size / 2;
    return (
        <View style={{ width: size, height: size }}>
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.pink400,
                    borderTopRightRadius: half,
                    borderBottomLeftRadius: half
                }}
            />
            <View
                style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: half,
                    height: half,
                    backgroundColor: colors.teal400,
                    borderTopLeftRadius: half,
                    borderBottomRightRadius: half
                }}
            />
        </View>
    );
}

function FormInput({
    label,
    required,
    value,
    onChangeText,
    placeholder,
    autoFocus,
    autoCapitalize,
    autoComplete,
    icon,
    iconColor,
    trailingIcon,
    trailingIconColor,
    error: fieldError
}: {
    label: string;
    required?: boolean;
    value: string;
    onChangeText: (text: string) => void;
    placeholder: string;
    autoFocus?: boolean;
    autoCapitalize?: "none" | "sentences" | "words" | "characters";
    autoComplete?: string;
    icon?: string;
    iconColor?: string;
    trailingIcon?: string;
    trailingIconColor?: string;
    error?: boolean;
}) {
    const [focused, setFocused] = useState(false);

    return (
        <View style={styles.fieldGroup}>
            <Text style={styles.fieldLabel}>
                {label}
                {required ? " *" : ""}
            </Text>
            <View
                style={[
                    styles.inputCard,
                    focused && styles.inputCardFocused,
                    fieldError && styles.inputCardError
                ]}
            >
                {icon && (
                    <MaterialCommunityIcons
                        name={icon as keyof typeof MaterialCommunityIcons.glyphMap}
                        size={18}
                        color={iconColor ?? colors.textTertiary}
                    />
                )}
                <TextInput
                    style={styles.input}
                    value={value}
                    onChangeText={onChangeText}
                    placeholder={placeholder}
                    placeholderTextColor={colors.textTertiary}
                    autoFocus={autoFocus}
                    autoCapitalize={autoCapitalize}
                    autoComplete={autoComplete as any}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                />
                {trailingIcon && (
                    <MaterialCommunityIcons
                        name={trailingIcon as keyof typeof MaterialCommunityIcons.glyphMap}
                        size={18}
                        color={trailingIconColor ?? colors.teal400}
                    />
                )}
            </View>
        </View>
    );
}

export default function RegisterScreen() {
    const router = useRouter();
    const { phone } = useLocalSearchParams<{ phone: string }>();
    const { pendingPhone, completeProfile } = useAuth();

    const [firstName, setFirstName] = useState("");
    const [middleName, setMiddleName] = useState("");
    const [lastName, setLastName] = useState("");
    const [address, setAddress] = useState("");
    const [addressInput, setAddressInput] = useState("");
    const [addressData, setAddressData] = useState<{
        formatted: string;
        lat: number;
        lng: number;
        line1: string;
        city: string;
        state: string;
        zip: string;
    } | null>(null);
    const [suggestions, setSuggestions] = useState<Array<{ description: string; placeId: string }>>(
        []
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addressError, setAddressError] = useState(false);
    const [addressFocused, setAddressFocused] = useState(false);
    const [resolvingAddress, setResolvingAddress] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const placesServiceRef = useRef<unknown>(null);

    const MAPS_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";

    // Pre-fill name from a landing-site lead the nurse created (web -> app
    // hand-off). Only fills empty fields, so it never clobbers user edits.
    useEffect(() => {
        const leadPhone = phone || pendingPhone;
        if (!leadPhone) {
            return;
        }
        let cancelled = false;
        void getLeadByPhone(leadPhone).then((lead) => {
            if (cancelled || !lead) {
                return;
            }
            setFirstName((prev) => prev || lead.firstName || "");
            setMiddleName((prev) => prev || lead.middleName || "");
            setLastName((prev) => prev || lead.lastName || "");
        });
        return () => {
            cancelled = true;
        };
    }, [phone, pendingPhone]);

    // Load Google Places JS API on web
    const autocompleteService = useRef<unknown>(null);

    useEffect(() => {
        if (Platform.OS !== "web" || !MAPS_KEY) {
            return;
        }

        type PlacesAPI = { AutocompleteService: new () => unknown };
        type GoogleMaps = { maps?: { places?: PlacesAPI } };

        const getPlaces = (): PlacesAPI | undefined =>
            ((window as unknown as Record<string, unknown>).google as GoogleMaps | undefined)?.maps
                ?.places;

        // If Places API is already loaded, use it directly
        const places = getPlaces();
        if (places) {
            autocompleteService.current = new places.AutocompleteService();
            return;
        }

        // Load Google Maps with Places library
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places`;
        script.async = true;
        script.onload = () => {
            const loaded = getPlaces();
            if (loaded) {
                autocompleteService.current = new loaded.AutocompleteService();
            }
        };
        document.head.appendChild(script);
    }, [MAPS_KEY]);

    const searchAddress = useCallback(
        (input: string) => {
            if (input.length < 3) {
                setSuggestions([]);
                return;
            }
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = setTimeout(() => {
                if (Platform.OS === "web" && autocompleteService.current) {
                    // Use JS Places API on web (avoids CORS)
                    const svc = autocompleteService.current as {
                        getPlacePredictions: (
                            req: {
                                input: string;
                                types: string[];
                                componentRestrictions: { country: string };
                            },
                            cb: (
                                results: Array<{ description: string; place_id: string }> | null
                            ) => void
                        ) => void;
                    };
                    svc.getPlacePredictions(
                        { input, types: ["address"], componentRestrictions: { country: "us" } },
                        (results) => {
                            if (results) {
                                setSuggestions(
                                    results.map((p) => ({
                                        description: p.description,
                                        placeId: p.place_id
                                    }))
                                );
                            }
                        }
                    );
                } else if (MAPS_KEY) {
                    // REST API fallback for native
                    fetch(
                        `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(input)}&types=address&components=country:us&key=${MAPS_KEY}`
                    )
                        .then((res) => res.json())
                        .then((data) => {
                            if (data.predictions) {
                                setSuggestions(
                                    data.predictions.map(
                                        (p: { description: string; place_id: string }) => ({
                                            description: p.description,
                                            placeId: p.place_id
                                        })
                                    )
                                );
                            }
                        })
                        .catch(() => {});
                }
            }, 300);
        },
        [MAPS_KEY]
    );

    // Fetch Place Details to get lat/lng + structured address components
    async function fetchPlaceDetails(placeId: string) {
        if (Platform.OS === "web") {
            // Web: use JS PlacesService
            return new Promise<typeof addressData>((resolve) => {
                const google = (window as unknown as Record<string, unknown>).google as
                    Record<string, unknown> | undefined;
                const maps = google?.maps as Record<string, unknown> | undefined;
                const places = maps?.places as Record<string, unknown> | undefined;
                if (!places) {
                    resolve(null);
                    return;
                }
                // PlacesService needs a DOM element
                if (!placesServiceRef.current) {
                    const PlacesService = places.PlacesService as new (el: HTMLElement) => unknown;
                    const el = document.createElement("div");
                    placesServiceRef.current = new PlacesService(el);
                }
                const svc = placesServiceRef.current as {
                    getDetails: (
                        req: { placeId: string; fields: string[] },
                        cb: (result: unknown, status: string) => void
                    ) => void;
                };
                svc.getDetails(
                    { placeId, fields: ["formatted_address", "geometry", "address_components"] },
                    (result: unknown, status: string) => {
                        if (status !== "OK" || !result) {
                            resolve(null);
                            return;
                        }
                        const r = result as {
                            formatted_address?: string;
                            geometry?: { location?: { lat: () => number; lng: () => number } };
                            address_components?: Array<{
                                short_name: string;
                                types: string[];
                            }>;
                        };
                        if (!r.geometry?.location) {
                            resolve(null);
                            return;
                        }
                        const comps = r.address_components ?? [];
                        const get = (type: string) =>
                            comps.find((c) => c.types.includes(type))?.short_name ?? "";
                        resolve({
                            formatted: r.formatted_address ?? "",
                            lat: r.geometry.location.lat(),
                            lng: r.geometry.location.lng(),
                            line1: `${get("street_number")} ${get("route")}`.trim(),
                            city: get("locality") || get("sublocality"),
                            state: get("administrative_area_level_1"),
                            zip: get("postal_code")
                        });
                    }
                );
            });
        }
        // Native: REST API
        try {
            const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${encodeURIComponent(placeId)}&fields=formatted_address,geometry,address_component&key=${MAPS_KEY}`;
            const res = await fetch(url);
            const data = await res.json();
            const result = data.result;
            if (!result?.geometry?.location) {
                return null;
            }
            const comps = result.address_components ?? [];
            const get = (type: string) =>
                comps.find((c: { types: string[]; short_name: string }) => c.types.includes(type))
                    ?.short_name ?? "";
            return {
                formatted: result.formatted_address ?? "",
                lat: result.geometry.location.lat as number,
                lng: result.geometry.location.lng as number,
                line1: `${get("street_number")} ${get("route")}`.trim(),
                city: get("locality") || get("sublocality"),
                state: get("administrative_area_level_1"),
                zip: get("postal_code")
            };
        } catch {
            return null;
        }
    }

    function handleAddressInput(text: string) {
        setAddressInput(text);
        setAddress("");
        setAddressData(null);
        setAddressError(false);
        searchAddress(text);
    }

    async function selectSuggestion(suggestion: { description: string; placeId: string }) {
        setAddressInput(suggestion.description);
        setSuggestions([]);
        setAddressError(false);
        setResolvingAddress(true);

        const details = await fetchPlaceDetails(suggestion.placeId);
        setResolvingAddress(false);

        if (details) {
            setAddress(details.formatted);
            setAddressInput(details.formatted);
            setAddressData(details);
        } else {
            setAddress("");
            setAddressData(null);
            setAddressError(true);
            setError("Could not verify address. Please try again.");
        }
    }

    async function handleSubmit() {
        const userPhone = phone || pendingPhone;

        // Validate all fields and show specific errors
        const missing: string[] = [];
        if (!firstName.trim()) {
            missing.push("first name");
        }
        if (!lastName.trim()) {
            missing.push("last name");
        }
        if (!addressData) {
            setAddressError(true);
            missing.push("address");
        }

        if (missing.length > 0) {
            setError(`Please fill in: ${missing.join(", ")}`);
            return;
        }

        if (!userPhone) {
            setError("Phone number missing. Please go back and try again.");
            return;
        }

        setLoading(true);
        setError(null);

        const result = await completeProfile({
            phone: userPhone,
            firstName: firstName.trim(),
            middleName: middleName.trim() || undefined,
            lastName: lastName.trim(),
            address: addressData!
        });

        setLoading(false);

        if (!result.success) {
            const msg = result.error ?? "Profile creation failed";
            // Check for expired verification
            if (
                msg.toLowerCase().includes("expired") ||
                msg.toLowerCase().includes("verification")
            ) {
                setError("Your verification expired. Please verify your phone again.");
                setTimeout(() => router.replace("/(auth)/login"), 3000);
                return;
            }
            // Check for conflict (duplicate phone)
            if (msg.toLowerCase().includes("already exists")) {
                setError("An account with this phone already exists. Please go back and log in.");
                return;
            }
            setError(msg);
        }
        // On success: context sets authStatus + user -> layout navigates to /(tabs)
    }

    return (
        <SafeAreaView style={styles.container}>
            <KeyboardAvoidingView
                style={styles.keyboardView}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                {/* Back */}
                <TouchableOpacity
                    onPress={() => router.replace("/(auth)/login")}
                    hitSlop={12}
                    style={styles.backButton}
                >
                    <MaterialCommunityIcons
                        name="arrow-left"
                        size={22}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>

                <ScrollView
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                >
                    {/* Logo */}
                    <View style={styles.logoArea}>
                        <PinwheelLogo size={52} />
                    </View>

                    <Text style={styles.title}>Complete your profile</Text>
                    <Text style={styles.subtitle}>Tell us about yourself</Text>

                    {/* First Name */}
                    <FormInput
                        label="First name"
                        required
                        value={firstName}
                        onChangeText={setFirstName}
                        placeholder="First name"
                        autoFocus
                        autoCapitalize="words"
                        autoComplete="given-name"
                    />

                    {/* Middle Name */}
                    <FormInput
                        label="Middle name"
                        value={middleName}
                        onChangeText={setMiddleName}
                        placeholder="Middle name (optional)"
                        autoCapitalize="words"
                        autoComplete="additional-name"
                    />

                    {/* Last Name */}
                    <FormInput
                        label="Last name"
                        required
                        value={lastName}
                        onChangeText={setLastName}
                        placeholder="Last name"
                        autoCapitalize="words"
                        autoComplete="family-name"
                    />

                    {/* Address with Google Places autocomplete */}
                    <View style={styles.fieldGroup}>
                        <Text style={styles.fieldLabel}>Address *</Text>
                        <View
                            style={[
                                styles.inputCard,
                                addressFocused && styles.inputCardFocused,
                                suggestions.length > 0 && styles.inputCardOpen,
                                addressError && styles.inputCardError
                            ]}
                        >
                            <MaterialCommunityIcons
                                name="map-marker-outline"
                                size={18}
                                color={
                                    addressError
                                        ? colors.error500
                                        : address
                                          ? colors.teal400
                                          : colors.textTertiary
                                }
                            />
                            <TextInput
                                style={styles.input}
                                value={addressInput}
                                onChangeText={handleAddressInput}
                                placeholder="Start typing your address..."
                                placeholderTextColor={colors.textTertiary}
                                onFocus={() => setAddressFocused(true)}
                                onBlur={() => setAddressFocused(false)}
                            />
                            {address ? (
                                <MaterialCommunityIcons
                                    name="check-circle"
                                    size={18}
                                    color={colors.teal400}
                                />
                            ) : null}
                        </View>

                        {/* Autocomplete suggestions */}
                        {suggestions.length > 0 && (
                            <View style={styles.suggestionsCard}>
                                {suggestions.map((s, idx) => (
                                    <TouchableOpacity
                                        key={s.placeId}
                                        style={[
                                            styles.suggestionRow,
                                            idx < suggestions.length - 1 && styles.suggestionBorder
                                        ]}
                                        onPress={() => selectSuggestion(s)}
                                        activeOpacity={0.7}
                                    >
                                        <MaterialCommunityIcons
                                            name="map-marker"
                                            size={16}
                                            color={colors.teal400}
                                        />
                                        <Text style={styles.suggestionText} numberOfLines={2}>
                                            {s.description}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        )}

                        {addressError && (
                            <View style={styles.addressErrorRow}>
                                <MaterialCommunityIcons
                                    name="alert-circle"
                                    size={14}
                                    color={colors.error500}
                                />
                                <Text style={styles.addressErrorText}>
                                    Please select an address from the suggestions
                                </Text>
                            </View>
                        )}

                        {!addressError && (
                            <Text style={styles.fieldHint}>
                                We use this to match you with nearby visits
                            </Text>
                        )}
                    </View>

                    {error && (
                        <View style={styles.errorRow}>
                            <MaterialCommunityIcons
                                name="alert-circle"
                                size={14}
                                color={colors.error500}
                            />
                            <Text style={styles.errorText}>{error}</Text>
                        </View>
                    )}

                    {/* Phone display */}
                    <View style={styles.phoneNote}>
                        <MaterialCommunityIcons
                            name="phone-check-outline"
                            size={16}
                            color={colors.teal400}
                        />
                        <Text style={styles.phoneNoteText}>
                            Phone verified:{" "}
                            {phone || pendingPhone
                                ? `(***) ***-${(phone || pendingPhone)!.slice(-4)}`
                                : ""}
                        </Text>
                    </View>
                </ScrollView>

                {/* Submit */}
                <View style={styles.bottomArea}>
                    <TouchableOpacity
                        activeOpacity={0.8}
                        onPress={handleSubmit}
                        disabled={loading}
                        style={styles.submitButtonWrapper}
                    >
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.submitButton}
                        >
                            <Text style={styles.submitText}>
                                {loading ? "Saving..." : "Continue"}
                            </Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </View>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    keyboardView: {
        flex: 1
    },
    backButton: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.md,
        alignSelf: "flex-start"
    },
    scrollContent: {
        paddingHorizontal: spacing.xl,
        paddingTop: spacing.lg,
        paddingBottom: spacing["2xl"],
        alignItems: "center"
    },
    logoArea: {
        marginBottom: spacing.lg
    },
    title: {
        fontSize: 22,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        textAlign: "center"
    },
    subtitle: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginTop: spacing.sm,
        marginBottom: spacing["2xl"],
        textAlign: "center"
    },
    fieldGroup: {
        marginBottom: spacing.base,
        width: "100%"
    },
    fieldLabel: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary,
        marginBottom: spacing.sm,
        letterSpacing: 0.3
    },
    inputCard: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 2,
        borderColor: colors.border,
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.md,
        gap: spacing.sm
    },
    inputCardFocused: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    inputCardError: {
        borderColor: colors.error400
    },
    inputCardOpen: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomWidth: 0
    },
    input: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textPrimary,
        padding: 0,
        flex: 1
    },
    suggestionsCard: {
        backgroundColor: colors.surface,
        borderWidth: 2,
        borderTopWidth: 1,
        borderColor: colors.border,
        borderBottomLeftRadius: radius.md,
        borderBottomRightRadius: radius.md,
        ...shadows.sm
    },
    suggestionRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.base
    },
    suggestionBorder: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    suggestionText: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textPrimary,
        flex: 1
    },
    addressErrorRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginTop: spacing.sm
    },
    addressErrorText: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.error500
    },
    fieldHint: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        marginTop: spacing.xs
    },
    errorRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        marginBottom: spacing.md,
        width: "100%"
    },
    errorText: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.error500
    },
    phoneNote: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.teal50,
        borderRadius: radius.md,
        padding: spacing.md,
        marginTop: spacing.md,
        width: "100%"
    },
    phoneNoteText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.teal700,
        flex: 1
    },
    bottomArea: {
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing["2xl"]
    },
    submitButtonWrapper: {
        width: "100%"
    },
    submitButton: {
        paddingVertical: 16,
        borderRadius: 20,
        alignItems: "center",
        ...shadows.brand
    },
    submitText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    }
});
